/**
 * Subscription API client — wrappers for `/v1/subscriptions/*` endpoints.
 *
 * Backend: `internal/payment/http_app/subscriptions.go` (PR #112, commit
 * 4850575).
 *
 * Routes:
 *   GET  /v1/subscriptions/plans         — tariff catalogue (#265)
 *   GET  /v1/subscriptions/me            — current tenant subscription
 *   POST /v1/subscriptions/provision     — owner: idempotent create on a plan
 *   GET  /v1/subscriptions/{id}          — subscription detail
 *   POST /v1/subscriptions/{id}/suspend  — admin or owner
 *   POST /v1/subscriptions/{id}/resume   — admin or owner
 *   POST /v1/subscriptions/{id}/cancel   — admin or owner
 */
import { customFetch } from './openapi-fetch.js';
import type {
  Subscription,
  SubscriptionActionRequest,
  SubscriptionPlanListResponse,
  ProvisionSubscriptionRequest,
} from '@hsc/types';

/** Tariff catalogue (Старт/Резорт/Сеть) with per-room pricing. */
export async function listSubscriptionPlans(): Promise<SubscriptionPlanListResponse> {
  return customFetch<SubscriptionPlanListResponse>({
    url: '/subscriptions/plans',
    method: 'GET',
  });
}

export async function getMySubscription(): Promise<Subscription> {
  return customFetch<Subscription>({
    url: '/subscriptions/me',
    method: 'GET',
  });
}

/**
 * Owner-only: idempotently create the tenant subscription on the chosen plan
 * (default Старт). Returns the existing subscription if one already exists.
 */
export async function provisionSubscription(
  req: ProvisionSubscriptionRequest,
): Promise<Subscription> {
  return customFetch<Subscription>({
    url: '/subscriptions/provision',
    method: 'POST',
    data: req,
  });
}

export async function getSubscription(id: string): Promise<Subscription> {
  return customFetch<Subscription>({
    url: `/subscriptions/${encodeURIComponent(id)}`,
    method: 'GET',
  });
}

export async function suspendSubscription(
  id: string,
  req: SubscriptionActionRequest = {},
): Promise<Subscription> {
  return customFetch<Subscription>({
    url: `/subscriptions/${encodeURIComponent(id)}/suspend`,
    method: 'POST',
    data: req,
  });
}

export async function resumeSubscription(
  id: string,
): Promise<Subscription> {
  return customFetch<Subscription>({
    url: `/subscriptions/${encodeURIComponent(id)}/resume`,
    method: 'POST',
    data: {},
  });
}

export async function cancelSubscription(
  id: string,
  req: SubscriptionActionRequest = {},
): Promise<Subscription> {
  return customFetch<Subscription>({
    url: `/subscriptions/${encodeURIComponent(id)}/cancel`,
    method: 'POST',
    data: req,
  });
}
